import type { CSSProperties, ReactElement } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, ic } from "../wizard-icons";

const K = keys.quickSetup;

export type SummaryRow = { label: string; value: string; valueDir?: "ltr" | "rtl" };

type DoneProps = {
  summary: SummaryRow[];
  logo: string | null;
  /** Active-locale draft shown/edited in the single editor; the shell still
   *  generates + saves BOTH `ar`/`en` under the hood (see QuickSetupWizard). */
  description: string;
  onDescChange: (v: string) => void;
  descLoading: boolean;
  descErr: boolean;
  onGoToDashboard: () => void;
  onAddProducts: () => void;
  submitting: boolean;
  submitError: string | null;
  onRetry: () => void;
  /** Mobile layout (matches the handoff's M3 frame): smaller title/padding,
   *  full-bleed card. */
  narrow?: boolean;
};

/**
 * Full-screen navy "Your store is ready." takeover (replaces the old
 * celebration/confetti + products-grid Done screen — products are always
 * empty at this point since product creation was moved out of the wizard).
 * Matches the handoff's FINAL/M3 frames: logo tile, title/subtitle, a single
 * white card with an editable draft description + ✓ summary rows, ONE gold
 * primary CTA, and a demoted text-link "go to dashboard".
 */
export function Done({
  summary,
  logo,
  description,
  onDescChange,
  descLoading,
  descErr,
  onGoToDashboard,
  onAddProducts,
  submitting,
  submitError,
  onRetry,
  narrow,
}: DoneProps): ReactElement {
  const t = useTranslations();
  const locale = useLocale();
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const animate = !reduce;

  const rise = (d: number): { className?: string; style: CSSProperties } =>
    animate ? { className: "qs-rise", style: { animationDelay: d + "s" } } : { style: {} };

  return (
    <div
      className="qs-celebrate"
      style={{
        position: "relative",
        minHeight: "100%",
        background: "var(--navy)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: narrow ? "32px 20px" : "48px 24px",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div className="qs-diamond" style={{ top: -180, insetInlineEnd: -140, width: 460, height: 460 }} />
      <div className="qs-diamond" style={{ bottom: -200, insetInlineStart: -160, width: 440, height: 440, opacity: 0.05 }} />

      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: narrow ? 20 : 28,
          width: "100%",
          maxWidth: 620,
        }}
      >
        {/* logo tile + title/subtitle */}
        <div
          className={rise(0.05).className}
          style={{ ...rise(0.05).style, display: "flex", flexDirection: "column", alignItems: "center", gap: narrow ? 10 : 12, textAlign: "center" }}
        >
          <div
            style={{
              width: narrow ? 60 : 72,
              height: narrow ? 60 : 72,
              borderRadius: narrow ? 15 : 18,
              background: "var(--bg)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {logo ? (
              <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <TriotyCube size={narrow ? 36 : 44} />
            )}
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: narrow ? 28 : 40,
              fontWeight: 800,
              letterSpacing: -0.4,
              color: "var(--on-navy)",
            }}
          >
            {t(K.done.title)}
          </h1>
          <p style={{ margin: 0, fontSize: narrow ? 14 : 16, color: "var(--on-navy-muted)", maxWidth: 460, lineHeight: 1.5 }}>
            {t(K.done.subtitle)}
          </p>
        </div>

        {/* white card: draft description + summary rows */}
        <div
          className={rise(0.14).className}
          style={{
            ...rise(0.14).style,
            width: "100%",
            background: "var(--bg)",
            borderRadius: narrow ? 14 : 16,
            padding: narrow ? "18px 20px" : "24px 28px",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              borderBottom: "1px solid var(--divider-2)",
              paddingBottom: 16,
              marginBottom: 4,
            }}
          >
            {descLoading ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "4px 0 2px" }}>
                {[100, 96, 70].map((w, i) => (
                  <span
                    key={i}
                    style={{
                      height: 12,
                      width: w + "%",
                      borderRadius: 6,
                      background: "linear-gradient(90deg, var(--bg-soft), var(--border), var(--bg-soft))",
                      backgroundSize: "200% 100%",
                      animation: "qs-shimmer 1.3s linear infinite",
                    }}
                  />
                ))}
              </div>
            ) : (
              <textarea
                value={description}
                onChange={(e) => onDescChange(e.target.value)}
                rows={3}
                dir={locale === "ar" ? "rtl" : "ltr"}
                style={{
                  width: "100%",
                  resize: "vertical",
                  border: "none",
                  outline: "none",
                  padding: 0,
                  fontSize: 15,
                  fontWeight: 600,
                  lineHeight: 1.55,
                  color: "var(--navy)",
                  background: "transparent",
                  fontFamily: "inherit",
                  textAlign: "start",
                }}
              />
            )}
            <span style={{ fontSize: 12, color: "var(--fine)" }}>
              {descErr ? t(K.description.draftFallback) : t(K.description.footer)}
            </span>
          </div>

          {summary.map((s, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "11px 0",
                borderBottom: i < summary.length - 1 ? "1px solid var(--divider)" : "none",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--muted)" }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "var(--ok-bg)",
                    color: "var(--ok)",
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  {ic(Icons.check, 11, { stroke: 3 })}
                </span>
                {s.label}
              </span>
              <span
                dir={s.valueDir}
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "var(--navy)",
                  textAlign: "end",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "60%",
                  // `unicode-bidi: plaintext` lets the browser pick the run's
                  // own base direction from its content (e.g. the "+20 120
                  // 888 8474" phone number stays LTR-ordered even inside an
                  // RTL page) without needing a `dir` override on every row —
                  // `valueDir` above is for rows that need an explicit force.
                  unicodeBidi: s.valueDir ? "normal" : "plaintext",
                }}
              >
                {s.value}
              </span>
            </div>
          ))}
        </div>

        {submitError && (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "12px 14px",
              borderRadius: 12,
              background: "rgba(225,29,72,0.12)",
              border: "1px solid rgba(225,29,72,0.35)",
              color: "var(--on-navy-danger)",
              fontSize: 13,
              fontWeight: 600,
              boxSizing: "border-box",
            }}
          >
            <span>{submitError}</span>
            <button
              onClick={onRetry}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--on-navy-danger)",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                textDecoration: "underline",
              }}
            >
              {t(K.done.retry)}
            </button>
          </div>
        )}

        {/* ONE gold primary CTA + demoted text-link */}
        <div
          className={rise(0.22).className}
          style={{ ...rise(0.22).style, display: "flex", flexDirection: "column", alignItems: "center", gap: 14, width: "100%" }}
        >
          <button
            onClick={onAddProducts}
            disabled={submitting}
            style={{
              height: narrow ? 52 : 56,
              width: "100%",
              maxWidth: 380,
              border: "none",
              borderRadius: 10,
              background: "var(--accent)",
              color: "var(--on-accent)",
              fontSize: 17,
              fontWeight: 800,
              fontFamily: "inherit",
              cursor: submitting ? "default" : "pointer",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {t(K.done.addFirstProduct)}
          </button>
          <span style={{ fontSize: 14, color: "var(--on-navy-faint)" }}>
            {t(K.done.orPrefix)}{" "}
            <button
              onClick={onGoToDashboard}
              disabled={submitting}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                fontSize: 14,
                fontWeight: 700,
                color: "var(--on-navy)",
                textDecoration: "underline",
                cursor: submitting ? "default" : "pointer",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? t(K.done.settingUpStore) : submitError ? t(K.done.goAnyway) : t(K.done.goToDashboard)}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

/** Trioty cube mark — stays Latin in both locales (brand decision), shown in
 *  the logo tile when the seller skipped uploading a logo. */
function TriotyCube({ size = 44 }: { size?: number }): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2l8 4.6v9.8L12 21l-8-4.6V6.6L12 2z"
        stroke="var(--accent-2)"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M12 2v19M4 6.6L12 11l8-4.4" stroke="var(--accent-2)" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}
